import React, { useState, useEffect, useMemo } from 'react';
import { parseLocalDate } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, AlertCircle, CheckCircle2, Loader2, Trophy, Swords, Coffee } from 'lucide-react';
import LeagueMatchCard from './LeagueMatchCard';
import LeagueMatchCarousel from './LeagueMatchCarousel';
import EliminationMatchCard from './EliminationMatchCard';
import TennisChallengeCard from './TennisChallengeCard';
import { LeagueScheduledMatch } from '@/hooks/useLeagueMatches';
import { EliminationMatch } from '@/hooks/useLeagueEliminationMatches';
import type { TennisChallengeEncounter } from '@/hooks/useTennisChallengeEncounters';

type EliminationPhase = 'octavos' | 'cuartos' | 'semifinal' | 'final';

type SelectionType = 
  | { type: 'encounter'; value: number }
  | { type: 'elimination'; value: EliminationPhase }
  | { type: 'rest'; value: number };

// Mapeo de fases a encounter_number en league_scheduled_matches
const phaseToEncounterNumber: Record<EliminationPhase, number> = {
  'octavos': 1000,
  'cuartos': 1001,
  'semifinal': 1002,
  'final': 1003
};

interface EncounterResultsSectionProps {
  selectedEncounter: number;
  onEncounterChange: (encounter: number) => void;
  availableEncounters: number[];
  encounterStatus: 'waiting_previous' | 'not_scheduled' | 'completed' | 'in_progress' | 'scheduled';
  userMatchForEncounter: any;
  userMatchesForEncounter: any[] | null;
  userTeam: { player1: string; player2?: string } | null;
  opponentTeam: { player1: string; player2?: string } | null;
  weeks: any[];
  league: any;
  userAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  opponentAttendanceStatus?: 'confirmed' | 'not_attending' | 'pending';
  isLoadingMatches: boolean;
  onWeekChange: (weekNum: number) => void;
  // Elimination props
  eliminationPhase?: EliminationPhase;
  hasEliminationPhase?: boolean;
  // NEW: All scheduled matches from the league (for elimination phases)
  allScheduledMatches?: LeagueScheduledMatch[];
  // Tennis singles challenge props
  isTennisSingles?: boolean;
  tennisChallengeEncounter?: TennisChallengeEncounter | null;
  challengeType?: string;
  challengePositionsAbove?: number | null;
  // Tennis singles elimination matches (from tennis_challenge_results)
  tennisSinglesEliminationMatches?: Record<string, EliminationMatch[]>;
  // Week labels for date display (tennis)
  weekLabels?: string[];
  restWeeks?: number[];
  // Chrono week sync props
  chronoWeekMapping?: Array<{ chronoWeek: number; type: 'encounter' | 'rest'; encounterNumber?: number }>;
  selectedChronoWeek?: number;
}

// Helper to get elimination phases based on starting phase
const getEliminationPhases = (startPhase: EliminationPhase): { key: EliminationPhase; label: string }[] => {
  const allPhases: { key: EliminationPhase; label: string }[] = [
    { key: 'octavos', label: 'Octavos de Final' },
    { key: 'cuartos', label: 'Cuartos de Final' },
    { key: 'semifinal', label: 'Semifinales' },
    { key: 'final', label: 'Final' }
  ];
  
  const startIndex = allPhases.findIndex(p => p.key === startPhase);
  return startIndex >= 0 ? allPhases.slice(startIndex) : [];
};

const EncounterResultsSection: React.FC<EncounterResultsSectionProps> = ({
  selectedEncounter,
  onEncounterChange,
  availableEncounters,
  encounterStatus,
  userMatchForEncounter,
  userMatchesForEncounter,
  userTeam,
  opponentTeam,
  weeks,
  league,
  userAttendanceStatus,
  opponentAttendanceStatus,
  isLoadingMatches,
  onWeekChange,
  eliminationPhase,
  hasEliminationPhase,
  allScheduledMatches,
  isTennisSingles,
  tennisChallengeEncounter,
  challengeType,
  challengePositionsAbove,
  tennisSinglesEliminationMatches,
  weekLabels,
  restWeeks,
  chronoWeekMapping,
  selectedChronoWeek
}) => {
  // Track selection type (encounter, elimination, or rest)
  const [selection, setSelection] = useState<SelectionType>({ type: 'encounter', value: selectedEncounter });

  // Sync with parent when selectedEncounter changes externally
  useEffect(() => {
    if (selection.type === 'encounter' && selection.value !== selectedEncounter) {
      setSelection({ type: 'encounter', value: selectedEncounter });
    }
  }, [selectedEncounter]);

  // Sync with chrono week from court booking
  useEffect(() => {
    if (!selectedChronoWeek || !chronoWeekMapping) return;
    const entry = chronoWeekMapping.find(m => m.chronoWeek === selectedChronoWeek);
    if (!entry) return;
    if (entry.type === 'rest') {
      setSelection({ type: 'rest', value: entry.chronoWeek });
    } else if (entry.type === 'encounter' && entry.encounterNumber) {
      setSelection({ type: 'encounter', value: entry.encounterNumber });
      onEncounterChange(entry.encounterNumber);
    }
  }, [selectedChronoWeek, chronoWeekMapping]);

  // Derive elimination matches from allScheduledMatches OR tennisSinglesEliminationMatches
  const eliminationMatches: EliminationMatch[] = useMemo(() => {
    if (selection.type !== 'elimination') {
      return [];
    }

    const phase = selection.value;

    // For tennis singles, prioritize data from tennis_challenge_results
    if (isTennisSingles && tennisSinglesEliminationMatches && tennisSinglesEliminationMatches[phase]?.length > 0) {
      return tennisSinglesEliminationMatches[phase];
    }

    // Fallback: use allScheduledMatches (for doubles/other formats)
    if (!allScheduledMatches) {
      return [];
    }

    const encounterNumber = phaseToEncounterNumber[phase];
    const phaseMatches = allScheduledMatches.filter(m => m.encounter_number === encounterNumber);
    
    if (phaseMatches.length === 0) {
      return [];
    }

    return phaseMatches.map((match) => {
      const team1Name = match.team1_player1_name 
        ? (match.team1_player2_name 
            ? `${match.team1_player1_name} / ${match.team1_player2_name}` 
            : match.team1_player1_name)
        : null;
      
      const team2Name = match.team2_player1_name 
        ? (match.team2_player2_name 
            ? `${match.team2_player1_name} / ${match.team2_player2_name}` 
            : match.team2_player1_name)
        : null;

      let formattedDate: string | null = null;
      if (match.scheduled_date) {
        const date = new Date(match.scheduled_date + 'T00:00:00');
        formattedDate = date.toLocaleDateString('es-ES', { 
          day: 'numeric', 
          month: 'short' 
        });
      }

      const formattedTime = match.scheduled_time 
        ? match.scheduled_time.slice(0, 5) 
        : null;

      return {
        id: match.id,
        phase: phase,
        matchNumber: match.match_number,
        team1: team1Name,
        team2: team2Name,
        score: null,
        winner: null,
        scheduledDate: formattedDate,
        scheduledTime: formattedTime,
        court: match.court_number ? `Cancha ${match.court_number}` : null,
        isUserMatch: false,
        isCompleted: match.status === 'completed'
      };
    }).sort((a, b) => a.matchNumber - b.matchNumber);
  }, [selection, allScheduledMatches, isTennisSingles, tennisSinglesEliminationMatches]);

  // Loading state for elimination (uses same loading as matches)
  const isLoadingElimination = isLoadingMatches && selection.type === 'elimination';

  // Get available elimination phases
  const availableEliminationPhases = hasEliminationPhase && eliminationPhase 
    ? getEliminationPhases(eliminationPhase)
    : [];

  // Resolve date label for the selected encounter (tennis only)
  const encounterDateLabel = useMemo(() => {
    if (!isTennisSingles || !weekLabels || weekLabels.length === 0 || selection.type !== 'encounter') {
      return null;
    }
    const rw = restWeeks || [];
    let encounterCount = 0;
    for (let i = 0; i < weekLabels.length; i++) {
      if (!rw.includes(i + 1)) {
        encounterCount++;
        if (encounterCount === selection.value) {
          return weekLabels[i];
        }
      }
    }
    return null;
  }, [isTennisSingles, weekLabels, restWeeks, selection]);

  // Handle selection change
  const handleSelectionChange = (value: string) => {
    // Check if it's a rest week
    if (value.startsWith('rest_')) {
      const weekNum = parseInt(value.replace('rest_', ''), 10);
      setSelection({ type: 'rest', value: weekNum });
      return;
    }
    // Check if it's an elimination phase
    if (value.startsWith('elim_')) {
      const phase = value.replace('elim_', '') as EliminationPhase;
      setSelection({ type: 'elimination', value: phase });
    } else {
      const encounterNum = parseInt(value, 10);
      setSelection({ type: 'encounter', value: encounterNum });
      onEncounterChange(encounterNum);
    }
  };

  // Get current selection value for the Select component
  const getCurrentSelectionValue = (): string => {
    if (selection.type === 'rest') {
      return `rest_${selection.value}`;
    }
    if (selection.type === 'elimination') {
      return `elim_${selection.value}`;
    }
    return selection.value.toString();
  };

  // Get display label for current selection
  const getCurrentSelectionLabel = (): string => {
    if (selection.type === 'rest') {
      return `☕ Descanso (Semana ${selection.value})`;
    }
    if (selection.type === 'elimination') {
      const phase = availableEliminationPhases.find(p => p.key === selection.value);
      return `🏆 ${phase?.label || selection.value}`;
    }
    return `Encuentro ${selection.value}`;
  };

  const getStatusBadge = () => {
    // For rest weeks
    if (selection.type === 'rest') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Coffee className="h-3 w-3 mr-1" />
          Semana de descanso
        </Badge>
      );
    }

    // For elimination phases
    if (selection.type === 'elimination') {
      if (isLoadingElimination) {
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-300">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Cargando
          </Badge>
        );
      }
      const allCompleted = eliminationMatches.length > 0 && eliminationMatches.every(m => m.isCompleted);
      const hasTeams = eliminationMatches.some(m => m.team1 || m.team2);
      
      if (allCompleted) {
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completado
          </Badge>
        );
      }
      if (hasTeams) {
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Calendar className="h-3 w-3 mr-1" />
            Programado
          </Badge>
        );
      }
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Por programar
        </Badge>
      );
    }

    // For regular encounters
    switch (encounterStatus) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completado
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/30">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            En progreso
          </Badge>
        );
      case 'scheduled':
        // For tennis singles, show challenge type instead of generic "Programado"
        if (isTennisSingles && challengeType) {
          const challengeTypeLabels: Record<string, string> = {
            'odds_vs_evens': 'Impares vs Pares',
            'evens_vs_odds': 'Pares vs Impares',
            'position_range': challengePositionsAbove 
              ? `Rango de ${challengePositionsAbove} posiciones` 
              : 'Rango de posiciones',
          };
          const label = challengeTypeLabels[challengeType] || challengeType;
          return (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
              <Swords className="h-3 w-3 mr-1" />
              {label}
            </Badge>
          );
        }
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Calendar className="h-3 w-3 mr-1" />
            Programado
          </Badge>
        );
      case 'waiting_previous':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-300">
            <Clock className="h-3 w-3 mr-1" />
            En espera
          </Badge>
        );
      case 'not_scheduled':
        return (
          <Badge className="bg-gray-100 text-gray-700 border-gray-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Por programar
          </Badge>
        );
    }
  };

  // Render elimination phase content
  const renderEliminationContent = () => {
    if (isLoadingElimination) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-2" />
          <p>Cargando partidos de eliminación...</p>
        </div>
      );
    }

    // If no matches found, show placeholder cards
    if (!eliminationMatches || eliminationMatches.length === 0) {
      const phaseLabel = availableEliminationPhases.find(p => p.key === selection.value)?.label || selection.value;
      const matchCount = selection.value === 'final' ? 1 : selection.value === 'semifinal' ? 2 : selection.value === 'cuartos' ? 4 : 8;
      
      return (
        <div className="space-y-4">
          {Array.from({ length: matchCount }, (_, i) => (
            <div key={i} className="bg-card rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {phaseLabel} {matchCount > 1 ? i + 1 : ''}
                  </span>
                </div>
                <Badge className="bg-gray-100 text-gray-700 border-gray-300">
                  Por programar
                </Badge>
              </div>
              
              <div className="text-center space-y-2 py-2">
                <p className="font-semibold text-muted-foreground italic">
                  Aún no asignado
                </p>
                <div className="flex items-center justify-center gap-2">
                  <div className="h-px w-8 bg-border"></div>
                  <span className="text-sm text-muted-foreground font-medium">vs</span>
                  <div className="h-px w-8 bg-border"></div>
                </div>
                <p className="font-semibold text-muted-foreground italic">
                  Aún no asignado
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Show actual matches
    return (
      <div className="space-y-4">
        {eliminationMatches.map(match => (
          <EliminationMatchCard 
            key={match.id}
            match={match}
            sport={league.sport}
          />
        ))}
      </div>
    );
  };

  // Render regular encounter content
  const renderEncounterContent = () => {
    // Tennis singles: use challenge encounter data
    if (isTennisSingles) {
      if (isLoadingMatches) {
        return (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mb-2" />
            <p>Cargando retos...</p>
          </div>
        );
      }

      if (tennisChallengeEncounter) {
        return <TennisChallengeCard encounter={tennisChallengeEncounter} weekNumber={selectedEncounter} dateLabel={encounterDateLabel || undefined} />;
      }

      return (
        <div className="bg-muted/50 border border-border rounded-lg p-6 text-center">
          <Swords className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h4 className="font-semibold text-foreground mb-2">
            Sin reto asignado
          </h4>
          <p className="text-muted-foreground text-sm">
            No tienes un reto asignado para esta semana.
          </p>
        </div>
      );
    }

    // Loading state
    if (isLoadingMatches) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-2" />
          <p>Cargando partidos...</p>
        </div>
      );
    }

    // Waiting for previous encounter to complete
    if (encounterStatus === 'waiting_previous') {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
          <Clock className="h-10 w-10 text-orange-500 mx-auto mb-3" />
          <h4 className="font-semibold text-orange-800 mb-2">
            Esperando resultados del Encuentro {selectedEncounter - 1}
          </h4>
          <p className="text-orange-600 text-sm">
            Los enfrentamientos de este encuentro se determinarán una vez se complete el encuentro anterior.
          </p>
        </div>
      );
    }

    // Not scheduled
    if (encounterStatus === 'not_scheduled') {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <Calendar className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-700 mb-2">
            Encuentro por programar
          </h4>
          <p className="text-gray-500 text-sm">
            Este encuentro aún no tiene partidos asignados.
          </p>
        </div>
      );
    }

    // Multiple matches - show carousel
    if (userMatchesForEncounter && userMatchesForEncounter.length > 1) {
      return (
        <LeagueMatchCarousel
          matches={userMatchesForEncounter}
          weeks={weeks}
          selectedWeek={selectedEncounter}
          onWeekChange={onWeekChange}
          userAttendanceStatus={userAttendanceStatus}
          opponentAttendanceStatus={opponentAttendanceStatus}
          isLoading={isLoadingMatches}
        />
      );
    }

    // Single match
    if (userMatchForEncounter && userTeam && opponentTeam) {
      const isPlayed = userMatchForEncounter.status === 'completed';
      const currentWeek = weeks.find(w => w.number === selectedEncounter);
      
      const matchDate = userMatchForEncounter.scheduled_date 
        ? parseLocalDate(userMatchForEncounter.scheduled_date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
        : currentWeek?.date || "";
      
      const matchTime = userMatchForEncounter.scheduled_time 
        ? userMatchForEncounter.scheduled_time.slice(0, 5)
        : currentWeek?.time || "18:00 - 20:00";
      
      return (
        <LeagueMatchCard
          playerName={userTeam.player1}
          partnerName={userTeam.player2 || undefined}
          opponent1={opponentTeam.player1}
          opponent2={opponentTeam.player2 || undefined}
          date={matchDate}
          time={matchTime}
          court={`Cancha ${userMatchForEncounter.court_number || "1"}`}
          modalidad={league.gameType}
          sport={league.sport}
          title={isPlayed ? "Resultado del Encuentro" : "Tu Reto"}
          isPlayed={isPlayed}
          opponentAttendanceStatus={opponentAttendanceStatus}
          userAttendanceStatus={userAttendanceStatus}
          isLoadingMatch={isLoadingMatches}
        />
      );
    }

    // No matches found for this encounter
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h4 className="font-semibold text-amber-800 mb-2">
          Sin partidos asignados
        </h4>
        <p className="text-amber-600 text-sm">
          No tienes partidos programados para este encuentro.
        </p>
      </div>
    );
  };

  return (
    <Card className="bg-card">
      <CardContent className="pt-6">
        {/* Persistent encounter/elimination selector */}
        <div className="flex items-center justify-between mb-4">
          <Select value={getCurrentSelectionValue()} onValueChange={handleSelectionChange}>
            <SelectTrigger className="w-auto min-w-[180px] bg-background">
              <SelectValue placeholder="Seleccionar encuentro" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {chronoWeekMapping && chronoWeekMapping.length > 0 ? (
                // Use chrono mapping to interleave encounters and rest weeks
                <>
                  {chronoWeekMapping.map(entry => (
                    entry.type === 'rest' ? (
                      <SelectItem key={`rest_${entry.chronoWeek}`} value={`rest_${entry.chronoWeek}`}>
                        ☕ Descanso (Semana {entry.chronoWeek})
                      </SelectItem>
                    ) : (
                      <SelectItem key={entry.encounterNumber} value={entry.encounterNumber!.toString()}>
                        Encuentro {entry.encounterNumber}
                      </SelectItem>
                    )
                  ))}
                  {availableEliminationPhases.map(phase => (
                    <SelectItem key={`elim_${phase.key}`} value={`elim_${phase.key}`}>
                      🏆 {phase.label}
                    </SelectItem>
                  ))}
                </>
              ) : (
                // Default: just encounters + elimination
                <>
                  {availableEncounters.map(enc => (
                    <SelectItem key={enc} value={enc.toString()}>
                      Encuentro {enc}
                    </SelectItem>
                  ))}
                  {availableEliminationPhases.map(phase => (
                    <SelectItem key={`elim_${phase.key}`} value={`elim_${phase.key}`}>
                      🏆 {phase.label}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {getStatusBadge()}
        </div>


        <Separator className="mb-4" />

        {selection.type === 'rest' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center space-y-2">
            <Coffee className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h4 className="font-semibold text-amber-800">
              Semana de descanso
            </h4>
            <p className="text-amber-600 text-sm">
              No hay encuentro programado esta semana.
            </p>
          </div>
        ) : selection.type === 'elimination' 
          ? renderEliminationContent() 
          : renderEncounterContent()
        }
      </CardContent>
    </Card>
  );
};

export default EncounterResultsSection;
